/**
 * Single source of truth for the console's presentation LOCALE + CURRENCY.
 *
 * Salonomy is UK-first: money renders in GBP, dates in en-GB. These two
 * constants are the ONE place a future multi-currency / multi-locale change
 * edits — every money/date *display* formatter reads them rather than
 * hardcoding 'en-GB' / 'GBP' at the call-site.
 *
 * Scope note: this governs *presentation* only. Machine-parsing internals that
 * must stay deterministic regardless of display locale (e.g. the en-US/en-CA
 * date-part extraction in lib/time.ts, or YYYY-MM-DD query keys) are NOT locale
 * constants and intentionally do not read these.
 */
export const LOCALE = 'en-GB';
export const CURRENCY = 'GBP';

/**
 * Build an `Intl.NumberFormat` for money in the configured locale + currency.
 * Pass any extra options (e.g. `maximumFractionDigits: 0` for whole pounds).
 * Money is always integer pence in, never JS float math — callers divide by 100
 * for the Intl boundary only (see lib/format.ts).
 */
export function currencyFormat(options: Intl.NumberFormatOptions = {}): Intl.NumberFormat {
  return new Intl.NumberFormat(LOCALE, { style: 'currency', currency: CURRENCY, ...options });
}

/** Build an `Intl.DateTimeFormat` in the configured display locale. */
export function dateFormat(options: Intl.DateTimeFormatOptions = {}): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(LOCALE, options);
}

/** Build an `Intl.NumberFormat` (non-currency, e.g. grouped integers) in the locale. */
export function numberFormat(options: Intl.NumberFormatOptions = {}): Intl.NumberFormat {
  return new Intl.NumberFormat(LOCALE, options);
}
